import { faArrowUpRightFromSquare, faFileLines } from '@fortawesome/pro-solid-svg-icons';
import { Typography } from '@mui/material';
import { useMemo } from 'react';

import DocumentLink from '../../components/DocumentLink/DocumentLink';
import Icon from '../../components/Icon/Icon';
import Link from '../../components/Link/Link';
import ListField from '../../components/ListField/ListField';
import Subtitle from '../../components/Subtitle/Subtitle';
import Summary from '../../components/Summary/Summary';
import SummaryRow from '../../components/Summary/SummaryRow';
import { formatNumberByThousands } from '../../helpers/formatNumberByThousands';
import { useAppSelector } from '../../hooks';
import useTranslation from '../../hooks/useTranslation';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import ConsolidationsSummaryRow from './ConsolidationsSummaryRow';
import FieldSummaryRow from './FieldSummaryRow';

const SubmitPageSidePanel = () => {
    const translate = useTranslation();
    const hasCoApplicant = useAppSelector(selectFieldStateByName)('coapplicantToggle')?.value === 'true';
    const consolidations = useAppSelector((state) => state.form.consolidations);
    const internalLoans = useAppSelector((state) => state.form.internalLoans);
    const internalLoanSum = useMemo(
        () => internalLoans.reduce((sum, debt) => sum + (debt.currentBalance?.amount || 0), 0),
        [internalLoans],
    );
    const loanAmount = Number.parseInt((useAppSelector(selectFieldStateByName)('amount')?.value as string) || '0');
    const externalConsolidationSum = (consolidations || []).reduce(
        (sum, debt) => sum + (debt.currentBalance?.amount || 0),
        0,
    );
    const totalConsolidationSum = internalLoanSum + externalConsolidationSum;
    const totalLoanAmount = Math.max(loanAmount, externalConsolidationSum) + internalLoanSum;
    const cashPayout = Math.max(loanAmount - externalConsolidationSum, 0);

    return (
        <>
            <Subtitle text={translate('page.submit.subtitle.details')} />
            <Summary label={translate('field.section.about-the-loan')}>
                {consolidations?.length > 0 || internalLoanSum > 0 ? (
                    <SummaryRow
                        label={translate('field.amount.label')}
                        value={`${formatNumberByThousands(totalLoanAmount)} ${translate('field.currency.label')}`}
                    />
                ) : (
                    <FieldSummaryRow
                        name="amount"
                        label={translate('field.amount.label')}
                        formatValue={(value) =>
                            `${formatNumberByThousands(value)} ${translate('field.currency.label')}`
                        }
                    />
                )}
                <FieldSummaryRow
                    name="tenor"
                    label={translate('field.tenor.label')}
                    formatValue={(value) => `${value / 12} ${value / 12 > 1 ? translate('years') : translate('year')}`}
                />
                <FieldSummaryRow name="loanReason" label={translate('page.submit.purpose-of-loan.label')} />
            </Summary>

            {(consolidations?.length > 0 || internalLoanSum > 0) && (
                <Summary label={translate('field.section.consolidation.label')} expand={false}>
                    <ConsolidationsSummaryRow consolidations={[...consolidations, ...internalLoans]} />
                    <SummaryRow
                        label={translate('field.consolidation-summary.repayment.label')}
                        value={`${formatNumberByThousands(totalConsolidationSum)} ${translate('field.currency.label')}`}
                    />
                    <SummaryRow
                        label={translate('field.consolidation-summary.payout.label')}
                        value={`${formatNumberByThousands(cashPayout)} ${translate('field.currency.label')}`}
                    />
                </Summary>
            )}

            <Summary label={translate('field.section.about-you')} expand={false}>
                <FieldSummaryRow
                    name="applicantGovernmentId"
                    label={translate('page.submit.applicant-government-id.label')}
                />
                <FieldSummaryRow
                    name="applicantEmailAddress"
                    label={translate('page.submit.applicant-email-address.label')}
                />
                <FieldSummaryRow
                    name="applicantMobileNumber"
                    label={translate('page.submit.applicant-mobile-number.label')}
                />
                <FieldSummaryRow
                    name="applicantMaritalStatus"
                    label={translate('page.submit.applicant-marital-status.label')}
                />
                <FieldSummaryRow
                    name="applicantNumberChildrenNotOfAge"
                    label={translate('page.submit.applicant-number-children-not-of-age.label')}
                />
                <FieldSummaryRow
                    name="applicantHabitationForm"
                    label={translate('page.submit.applicant-habitation-form.label')}
                />
                <FieldSummaryRow
                    name="applicantAccomodationCost"
                    label={translate('page.submit.applicant-rent-share.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantEmploymentType"
                    label={translate('page.submit.applicant-employment-type.label')}
                />
                <FieldSummaryRow
                    name="applicantMonthlyGrossIncome"
                    label={translate('field.applicant-monthly-gross-income.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantYearlyGrossIncome"
                    label={translate('page.submit.applicant-yearly-gross-income.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantMonthlyIncomeOther"
                    label={translate('page.submit.applicant-monthly-income-other.label')}
                    formatValue={(value) => formatNumberByThousands(value)}
                />
                <FieldSummaryRow
                    name="applicantMortgageDebt"
                    label={translate('page.submit.applicant-mortage-debt.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantMortgageMonthlyCost"
                    label={translate('page.submit.applicant-mortage-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLoanSum"
                    label={translate('page.submit.applicant-car-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLoanMonthlyCost"
                    label={translate('page.submit.applicant-car-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCarLeasingMonthlyCost"
                    label={translate('page.submit.applicant-car-leasing-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantStudentLoanSum"
                    label={translate('page.submit.applicant-student-loan-sum.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantStudentLoanMonthlyCost"
                    label={translate('page.submit.applicant-student-loan-monthly-cost.label')}
                    formatValue={(value) => `${formatNumberByThousands(value)} ${translate('field.currency.label')}`}
                />
                <FieldSummaryRow
                    name="applicantCreditRemarks"
                    label={translate('field.applicant-credit-remarks.label')}
                />
            </Summary>
            {hasCoApplicant && (
                <Summary label={translate('page.submit.about-coapplicant.label')} expand={false}>
                    <FieldSummaryRow
                        name="coapplicantGovernmentId"
                        label={translate('page.submit.coapplicant-government-id.label')}
                    />
                    <FieldSummaryRow
                        name="coapplicantEmailAddress"
                        label={translate('page.submit.coapplicant-email-adress.label')}
                    />
                    <FieldSummaryRow
                        name="coapplicantMobileNumber"
                        label={translate('page.submit.coapplicant-mobile-number.label')}
                    />
                </Summary>
            )}

            <Subtitle text={translate('page.submit.subtitle.documents')} />
            <ListField
                name="commonTermsAndSekki"
                items={[
                    {
                        index: '1',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.common-terms'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.common-terms')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                    {
                        index: '2',
                        icon: <Icon icon={faFileLines} />,
                        label: (
                            <Link onClick={() => window.open(translate('page.submit.link.sekki'), '_blank')}>
                                <Typography variant="body2">{translate('page.submit.label.sekki')}</Typography>
                            </Link>
                        ),
                        endIcon: <Icon icon={faArrowUpRightFromSquare} size="sm" />,
                    },
                ]}
            />
            <Subtitle text={translate('page.submit.subtitle.gdpr')} />
            <Typography variant="body2"> {translate('page.submit.body-text.gdpr')}</Typography>
            <Typography variant="body2">
                <DocumentLink href={translate('page.submit.link.gdpr')} text={translate('page.submit.label.gdpr')} />
            </Typography>
        </>
    );
};

export default SubmitPageSidePanel;
